import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Minus, Trash2, Search } from "lucide-react";
import { format, addDays } from "date-fns";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { gbp } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { Sap } from "@/components/StatusBadge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const Basket = ({ basket, setQty, total }) => (
  <div className="space-y-2" data-testid="basket-list">
    {basket.length === 0 && <div className="py-8 text-center text-sm text-slate-500">Add products from the catalogue</div>}
    {basket.map(({ p, qty }) => (
      <div key={p.sap_code} className="flex items-center gap-2 border-b pb-2" data-testid={`basket-item-${p.sap_code}`}>
        <div className="min-w-0 flex-1"><Sap code={p.sap_code} /><div className="truncate text-xs">{p.title}</div></div>
        <button onClick={() => setQty(p, qty - 1)} className="rounded p-1 hover:bg-slate-100" data-testid={`basket-dec-${p.sap_code}`}><Minus className="h-3 w-3" /></button>
        <span className="w-6 text-center text-sm tabular-nums" data-testid={`basket-qty-${p.sap_code}`}>{qty}</span>
        <button onClick={() => setQty(p, qty + 1)} className="rounded p-1 hover:bg-slate-100" data-testid={`basket-inc-${p.sap_code}`}><Plus className="h-3 w-3" /></button>
        <span className="w-20 text-right text-sm font-semibold tabular-nums">{gbp(p.price * qty)}</span>
        <button onClick={() => setQty(p, 0)} className="p-1 text-slate-400 hover:text-red-600" data-testid={`basket-remove-${p.sap_code}`}><Trash2 className="h-3.5 w-3.5" /></button>
      </div>))}
    <div className="flex justify-between pt-2 font-display text-xl font-bold"><span>Total</span><span data-testid="basket-total">{gbp(total)}</span></div>
  </div>
);

export default function NewOrder() {
  const { user, meta, refreshMeta } = useAuth();
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("all");
  const [store, setStore] = useState(user.store_code || meta.stores[0]?.code);
  const [conc, setConc] = useState(user.concession_code || "none");
  const [delivery, setDelivery] = useState(format(addDays(new Date(), 5), "yyyy-MM-dd"));
  const [cc, setCc] = useState("");
  const [basket, setBasket] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data.filter((p) => p.active))); }, []);
  const shown = useMemo(() => products.filter((p) => (cat === "all" || p.category === cat) &&
    (`${p.sap_code} ${p.title}`.toLowerCase().includes(search.toLowerCase()))), [products, cat, search]);
  const concs = meta.concessions.filter((c) => c.store_code === store);
  const setQty = (p, qty) => setBasket((b) => qty <= 0 ? b.filter((x) => x.p.sap_code !== p.sap_code)
    : b.some((x) => x.p.sap_code === p.sap_code) ? b.map((x) => x.p.sap_code === p.sap_code ? { p, qty } : x) : [...b, { p, qty }]);
  const total = basket.reduce((s, x) => s + x.p.price * x.qty, 0);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/orders", { store_code: store, concession_code: conc === "none" ? null : conc, delivery_date: delivery,
        cost_centre: cc, lines: basket.map((x) => ({ sap_code: x.p.sap_code, qty: x.qty })) });
      toast.success(`Order ${data.order_number} submitted for approval`);
      refreshMeta();
      nav("/orders");
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Ordering" title="Create a new order" />
      <div className="grid gap-6 xl:grid-cols-[1fr_380px]">
        <div className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <div className="relative min-w-[220px] flex-1"><Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <Input className="bg-white pl-9" placeholder="Search SAP code or product…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="new-order-search" /></div>
            <Select value={cat} onValueChange={setCat}><SelectTrigger className="w-52 bg-white" data-testid="new-order-category"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All categories</SelectItem>{meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 2xl:grid-cols-3" data-testid="new-order-products">
            {shown.map((p) => {
              const inB = basket.find((x) => x.p.sap_code === p.sap_code);
              return (
                <div key={p.sap_code} className={`flex flex-col rounded-lg border bg-white p-4 transition-[border-color,box-shadow] duration-200 hover:shadow-md ${inB ? "border-[#00573F]" : ""}`}>
                  <div className="flex items-center justify-between"><Sap code={p.sap_code} /><span className="text-[10px] uppercase tracking-wider text-slate-400">{p.category}</span></div>
                  <div className="mt-2 flex-1 text-sm font-semibold leading-snug">{p.title}</div>
                  <div className="mt-1 text-xs text-slate-500">{p.pack_qty} per {p.per} · {p.stock.toLocaleString()} in stock</div>
                  <div className="mt-3 flex items-center justify-between"><span className="font-display text-lg font-bold">{gbp(p.price)}</span>
                    <Button size="sm" variant={inB ? "outline" : "default"} onClick={() => setQty(p, (inB?.qty || 0) + 1)} data-testid={`add-to-basket-${p.sap_code}`}>
                      <Plus className="mr-1 h-3.5 w-3.5" />{inB ? `Add (${inB.qty})` : "Add"}</Button></div>
                </div>);
            })}
          </div>
        </div>
        <div className="h-fit space-y-4 rounded-lg border bg-white p-5 xl:sticky xl:top-24">
          <h3 className="font-display text-lg font-semibold">Order details</h3>
          <div className="space-y-1"><span className="label-xs">Deliver to store</span>
            <Select value={store} onValueChange={(v) => { setStore(v); setConc("none"); }} disabled={meta.stores.length < 2}>
              <SelectTrigger data-testid="new-order-store"><SelectValue /></SelectTrigger>
              <SelectContent>{meta.stores.map((s) => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}</SelectContent></Select></div>
          {concs.length > 0 && (
            <div className="space-y-1"><span className="label-xs">Concession</span>
              <Select value={conc} onValueChange={setConc} disabled={user.role === "concession_manager"}>
                <SelectTrigger data-testid="new-order-concession"><SelectValue /></SelectTrigger>
                <SelectContent>{user.role !== "concession_manager" && <SelectItem value="none">Store order (no concession)</SelectItem>}
                  {concs.map((c) => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}</SelectContent></Select></div>)}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><span className="label-xs">Delivery date</span><Input type="date" value={delivery} onChange={(e) => setDelivery(e.target.value)} data-testid="new-order-delivery-date" /></div>
            <div className="space-y-1"><span className="label-xs">Cost centre</span><Input placeholder="e.g. FOODHALL" value={cc} onChange={(e) => setCc(e.target.value)} data-testid="new-order-cost-centre" /></div>
          </div>
          <Basket basket={basket} setQty={setQty} total={total} />
          <Button className="w-full bg-[#FF0000] hover:bg-[#D60000]" disabled={!basket.length || busy} onClick={submit} data-testid="submit-order-btn">Submit for approval</Button>
        </div>
      </div>
    </div>
  );
}
